import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { companyInfo } from '../data/siteData';
import { imageUrl } from '../lib/images';

export default function SkurPage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const images = [
    { src: "/_assets/pxl-20260529-082343854.jpg", title: "Skur og stålkonstruktion med koksgrå beklædning" },
    { src: "/_assets/295322415-584214203079039-442631182103604215-n-0.jpg", title: "Skur i komposit" },
    { src: "/_assets/thumbnail-1000004142-0.jpg", title: "Redskabsskur og affaldsskjul" },
    { src: "/_assets/thumbnail-1000004145-0_fe8d348a.jpg", title: "Skur med integreret carport" },
    { src: "/_assets/thumbnail-img20250813095918-0.jpg", title: "Robust skurkonstruktion" },
    { src: "/_assets/thumbnail-img20250813100319-0.jpg", title: "Skur i antracit komposit" },
    { src: "/_assets/thumbnail-img20250813100327-0.jpg", title: "Komplet skurløsning" },
  ];

  return (
    <>
      <SEO
        title="Skur – praktisk opbevaring i flot design | Hovendals"
        description="Vi tilbyder skræddersyede løsninger på et skur i robuste materialer Dit skur bliver både praktisk, holdbart og pænt at se på. Uanset om du ønsker et enkelt redskabsskur eller en større løsning kombineret med carport eller terrasse, tilpasser vi byggeriet til dine behov. Vi kan tilbyde dig:"
        ogImage="/_assets/thumbnail-1000004145-0-0.jpg"
      />

      {/* Hero */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl("/_assets/thumbnail-1000004145-0-0.jpg", "width=1600,quality=80,format=auto")}
            alt="Skur"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#004fa4]/20 text-[#c2ccdf] border border-[#004fa4]/30">
              <i className="fa-sharp fa-solid fa-shed" aria-hidden="true"></i>
              <span>Praktisk opbevaring</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Skur – praktisk opbevaring i flot design
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
              Et skur er den ideelle løsning, når du har brug for ekstra plads til haveredskaber, cykler, brænde eller andre ting, der skal opbevares tørt og sikkert. Hos Hovendals bygger vi skure, der ikke blot er funktionelle, men også matcher husets og havens udtryk.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Robuste materialer</span>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                  Vi tilbyder skræddersyede løsninger på et skur i robuste materialer
                </h2>
              </div>
              <p className="text-slate-700 leading-relaxed text-base">
                Dit skur bliver både praktisk, holdbart og pænt at se på. Uanset om du ønsker et enkelt redskabsskur eller en større løsning kombineret med carport eller terrasse, tilpasser vi byggeriet til dine behov.
              </p>

              <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <h3 className="font-bold text-slate-900 text-sm uppercase tracking-wider mb-3">
                  Vi kan tilbyde dig:
                </h3>
                <ul className="space-y-2.5 text-sm text-slate-700 font-medium">
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Skræddersyede skure tilpasset dine behov</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Bygget i robuste materialer, der tåler alt slags vejr</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Mulighed for integreret redskabs- eller opbevaringsrum</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Funktionelt design kombineret med flot æstetik</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                    <span>Langtidsholdbare løsninger med minimal vedligeholdelse</span>
                  </li>
                </ul>
              </div>

              <p className="text-slate-700 leading-relaxed text-sm">
                Med et skur fra Hovendals får du en kombination af funktionalitet, kvalitet og æstetik – en investering, der giver orden og overblik i hverdagen.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="px-6 py-3 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
                  <span>Ring: {companyInfo.phone}</span>
                </a>
                <Link
                  to="/kontakt"
                  className="px-6 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all flex items-center gap-2"
                >
                  <i className="fa-sharp fa-solid fa-paper-plane" aria-hidden="true"></i>
                  <span>Få et uforpligtende tilbud</span>
                </Link>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-slate-100">
              <img
                src={imageUrl("/_assets/carport-skur.webp", "width=1200,quality=85,format=auto")}
                alt="Moderne stålskur og overdækning"
                className="w-full h-auto aspect-[16/10] object-cover"
              />
            </div>
          </div>

          {/* Galleri */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Eksempler på skure udført af Hovendals
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Klik på et billede for at se forstørret visning.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative h-48 rounded-xl overflow-hidden bg-slate-100 cursor-pointer shadow-sm hover:shadow-md transition-all"
                >
                  <img
                    src={imageUrl(img.src, "width=600,quality=80,format=auto")}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <span className="p-2.5 rounded-full bg-[#143463]/90 text-white text-sm">
                      <i className="fa-sharp fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                    </span>
                  </div>
                  {img.title && (
                    <div className="absolute bottom-2 left-2 right-2 bg-slate-950/70 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded truncate">
                      {img.title}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <QuoteSection />

      {/* Lightbox modal */}
      <LightboxModal
        images={images}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(index) => setLightboxIndex(index)}
      />
    </>
  );
}
